// @flow
import * as rudder from 'rudder-sdk-js';

import type { EventSink } from '../event-sink';
import type { UserEvent } from '../user-event';
import {
  INTERACTION,
  NAVIGATE,
  REDUX_ACTION,
  WHITEBOARD_REDUX_ACTION,
} from '../user-event';

import type { User } from 'modules/user';
import type { Identifier } from 'modules/ecmascript';

export type Specification = $ReadOnly<{|
  /**
   * The org-level Data Plane URL provided by rudderstack.
   *
   * @see https://app.rudderstack.com/home
   */
  dataPlaneUrl: string,
  /**
   * The per-source Write Key provided by rudderstack.
   *
   * Should be available on the source details page of a rudderstack source.
   */
  writeKey: string,
|}>;

export type GenericEvent = $ReadOnly<{ ... }>;

export const sendUserEventToRudderstack: (
  UserEvent,
  client: string | null,
) => void = (userEvent, client) => {
  switch (userEvent.type) {
    case NAVIGATE:
      rudder.group('brand_id', { brand_id: client, group_type: 'brand_id' });
      rudder.page(null, userEvent.payload.to, undefined, { client });
      return;
    case INTERACTION:
      rudder.group('brand_id', { brand_id: client, group_type: 'brand_id' });
      rudder.track(
        userEvent.payload.interaction,
        (({
          interaction: _interaction,
          ...rest
        }: {
          +interaction: string,
          ...
        }) => rest)(userEvent.payload),
        { client },
      );
      return;
    case REDUX_ACTION:
      rudder.group('brand_id', { brand_id: client, group_type: 'brand_id' });
      rudder.track('Redux Action', userEvent.payload, { client });
      return;
    case WHITEBOARD_REDUX_ACTION:
      rudder.group('brand_id', { brand_id: client, group_type: 'brand_id' });
      rudder.track('Whiteboard Action', userEvent.payload, { client });
      return;
    default:
      (userEvent.type: empty);
      throw new Error(`Unhandled UserEvent type: ${userEvent.type}`);
  }
};

class RudderstackEventSink implements EventSink {
  specification: Specification;

  dataLayerName: Identifier;

  dataLayer: Array<mixed>;

  user: User | null;

  constructor(specification: Specification) {
    // eslint-disable-next-line fp/no-mutation
    this.specification = specification;
    rudder.load(this.specification.writeKey, this.specification.dataPlaneUrl, {
      sessions: {
        autoTrack: true,
      },
    });
  }

  pushStuffToDataLayer(..._args: $ReadOnlyArray<any>) {
    // apparently google _really_ wants this to be an arguments object, a regular array won't
    // trigger collection.
    // eslint-disable-next-line fp/no-mutating-methods, fp/no-arguments, prefer-rest-params
    this.dataLayer.push(arguments);
  }

  setUser(user: User | null, client: string | null) {
    // eslint-disable-next-line fp/no-mutation
    this.user = user;

    if (!user) {
      rudder.reset();
      return;
    }

    rudder.identify(
      // eslint-disable-next-line no-underscore-dangle
      user._id,
      { type: user.type },
      { client },
    );
  }

  handleEvent(event: UserEvent, user: User | null, client: string | null) {
    if (user && !this.user) this.setUser(user, client);
    sendUserEventToRudderstack(event, client);
  }
}

export const fromSpecification: (Specification) => RudderstackEventSink = (
  specification,
) => new RudderstackEventSink(specification);

export default RudderstackEventSink;
